import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// ── Color palettes per note (3 blob colors each) ─────────────────────────────
const NOTE_PALETTES = {
  Sa:  ['#f43f5e', '#e879f9', '#fb7185'],
  Re:  ['#f97316', '#fbbf24', '#fb923c'],
  Ga:  ['#a3e635', '#4ade80', '#86efac'],
  Ma:  ['#2dd4bf', '#06b6d4', '#67e8f9'],
  Pa:  ['#38bdf8', '#818cf8', '#60a5fa'],
  Dha: ['#8b5cf6', '#c084fc', '#a78bfa'],
  Ni:  ['#d946ef', '#a21caf', '#e879f9'],
  Sā:  ['#fde68a', '#fef9c3', '#fcd34d'],
};

// ── Blob drift keyframes injected once ───────────────────────────────────────
const BLOB_KEYFRAMES = `
@keyframes drift1 {
  0%,100%{transform:translate(0px,0px) scale(1)}
  25%{transform:translate(60px,-40px) scale(1.12)}
  50%{transform:translate(-30px,55px) scale(0.9)}
  75%{transform:translate(45px,25px) scale(1.08)}
}
@keyframes drift2 {
  0%,100%{transform:translate(0px,0px) scale(0.95)}
  30%{transform:translate(-55px,35px) scale(1.1)}
  60%{transform:translate(40px,-50px) scale(0.88)}
  80%{transform:translate(-20px,20px) scale(1.05)}
}
@keyframes drift3 {
  0%,100%{transform:translate(0px,0px) scale(1.05)}
  20%{transform:translate(35px,60px) scale(0.92)}
  55%{transform:translate(-60px,-20px) scale(1.15)}
  80%{transform:translate(20px,-45px) scale(0.95)}
}
@keyframes drift4 {
  0%,100%{transform:translate(0px,0px) scale(0.9)}
  35%{transform:translate(-40px,-55px) scale(1.1)}
  65%{transform:translate(55px,30px) scale(0.85)}
  85%{transform:translate(-25px,40px) scale(1.0)}
}
@keyframes drift5 {
  0%,100%{transform:translate(0px,0px) scale(1.08)}
  40%{transform:translate(50px,45px) scale(0.88)}
  70%{transform:translate(-35px,-35px) scale(1.12)}
}
@keyframes pulseAura {
  0%,100%{opacity:0.45}
  50%{opacity:0.65}
}
@keyframes fadeSlideUp {
  from{opacity:0;transform:translateY(12px)}
  to{opacity:1;transform:translateY(0)}
}
@keyframes scaleIn {
  from{opacity:0;transform:scale(0.85)}
  to{opacity:1;transform:scale(1)}
}
`;

// Blob layout: [animName, duration, top%, left%, size, colorIdx]
const BLOB_DEFS = [
  ['drift1', '13s', '15%', '10%',  320, 0],
  ['drift2', '17s', '55%', '65%',  380, 1],
  ['drift3', '11s', '30%', '50%',  260, 2],
  ['drift4', '19s', '70%', '5%',   300, 0],
  ['drift5', '7s',  '5%', '70%',   220, 1],
  ['drift1', '23s', '80%', '40%',  350, 2],
];

const PHASE_DURATION = 16; // seconds per note

// ── Lightweight YIN pitch detector (adapted from LivePitchViz) ───────────────
function detectPitchYIN(buf, sampleRate) {
  let rms = 0;
  for (let i = 0; i < buf.length; i++) rms += buf[i] * buf[i];
  rms = Math.sqrt(rms / buf.length);
  if (rms < 0.004) return null;

  const maxLag = Math.min(Math.floor(sampleRate / 70), buf.length - 1);
  const minLag = Math.max(Math.floor(sampleRate / 1100), 2);
  const d = new Float32Array(maxLag + 1);
  const cmndf = new Float32Array(maxLag + 1);
  for (let tau = 1; tau <= maxLag; tau++) {
    let sum = 0;
    for (let i = 0; i + tau < buf.length; i++) {
      const diff = buf[i] - buf[i + tau]; sum += diff * diff;
    }
    d[tau] = sum;
  }
  cmndf[0] = 1;
  let runSum = 0;
  for (let tau = 1; tau <= maxLag; tau++) {
    runSum += d[tau];
    cmndf[tau] = d[tau] * tau / (runSum || 1);
  }
  let best = -1;
  for (let tau = minLag; tau <= maxLag; tau++) {
    if (cmndf[tau] < 0.18) {
      while (tau + 1 <= maxLag && cmndf[tau + 1] < cmndf[tau]) tau++;
      best = tau; break;
    }
  }
  if (best === -1) {
    let minV = 1e9;
    for (let tau = minLag; tau <= maxLag; tau++) {
      if (cmndf[tau] < minV) { minV = cmndf[tau]; best = tau; }
    }
  }
  if (best <= 0) return null;
  const tM = Math.max(minLag, best - 1), tP = Math.min(maxLag, best + 1);
  const a = cmndf[tM], b = cmndf[best], c = cmndf[tP];
  const shift = (c - a) / (2 * (2 * b - a - c) || 1);
  const hz = sampleRate / (best + shift);
  return hz > 60 && hz < 1200 ? hz : null;
}

// ── Resonance hook: mic → pitch → proximity to target note (±forgiving) ──────
// Returns { resonance: 0..1, energy: 0..1 }
// • resonance = how close detected pitch is to targetHz (octave-tolerant, smooth)
// • energy    = mic volume level (0..1, smoothed)
// Tolerances are intentionally wide: ±250 cents still scores > 0 to avoid stress
function useVoiceResonance(targetHz) {
  const [resonance, setResonance] = useState(0);
  const [energy, setEnergy]       = useState(0);
  const refs = useRef({ stream: null, ctx: null, analyser: null, buf: null, raf: null });
  const resRef = useRef(0);
  const engRef = useRef(0);
  const lastSetRef = useRef(0);
  // Always-current ref so the rAF closure sees note changes without a restart
  const targetHzRef = useRef(targetHz);
  useEffect(() => { targetHzRef.current = targetHz; }, [targetHz]);

  // Start microphone once
  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ audio: true, video: false })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        const src = ctx.createMediaStreamSource(stream);
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        refs.current = { stream, ctx, analyser, buf, raf: null };

        const tick = () => {
          refs.current.raf = requestAnimationFrame(tick);
          analyser.getFloatTimeDomainData(buf);

          // RMS energy
          let rms = 0;
          for (let i = 0; i < buf.length; i++) rms += buf[i] * buf[i];
          const rawEng = Math.min(1, Math.sqrt(rms / buf.length) * 12);
          engRef.current = engRef.current * 0.88 + rawEng * 0.12;

          // Pitch → resonance
          let rawRes = 0;
          const tgt = targetHzRef.current;
          if (tgt) {
            const hz = detectPitchYIN(buf, ctx.sampleRate);
            if (hz) {
              // Octave-tolerant: check target ±2 octaves, take best match
              let minCents = Infinity;
              for (let shift = -2; shift <= 2; shift++) {
                const ref = tgt * Math.pow(2, shift);
                const cents = Math.abs(1200 * Math.log2(hz / ref));
                if (cents < minCents) minCents = cents;
              }
              // Very forgiving curve: 0¢=1.0, 150¢=0.6, 300¢=0.15, >400¢=0
              rawRes = minCents < 400
                ? Math.max(0, 1 - Math.pow(minCents / 300, 1.4))
                : 0;
            }
          }
          // Resonance only active when voice is audible
          const voiceActive = engRef.current > 0.08;
          resRef.current = resRef.current * 0.90 + (voiceActive ? rawRes : 0) * 0.10;

          // Push to React state at ~10 fps to avoid thrashing renders
          const now = performance.now();
          if (now - lastSetRef.current > 100) {
            lastSetRef.current = now;
            setResonance(parseFloat(resRef.current.toFixed(3)));
            setEnergy(parseFloat(engRef.current.toFixed(3)));
          }
        };
        tick();
      })
      .catch(() => {}); // silent — no mic permission = no visualization, no stress

    return () => {
      cancelled = true;
      const { stream, ctx, raf } = refs.current;
      if (raf) cancelAnimationFrame(raf);
      stream?.getTracks().forEach(t => t.stop());
      ctx?.close();
      refs.current = { stream: null, ctx: null, analyser: null, buf: null, raf: null };
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { resonance, energy };
}

// ── Breathing phase ──────────────────────────────────────────────────────────
function getPhase(elapsed) {
  if (elapsed < 3)  return 'breathe';
  if (elapsed < 13) return 'sing';
  return 'release';
}

const PHASE_TEXT = {
  breathe: 'Inhale…',
  sing:    'Sing…',
  release: 'Let go…',
};

// ── SVG Arc timer ────────────────────────────────────────────────────────────
const R = 44;
const CIRC = 2 * Math.PI * R;

function ArcTimer({ progress }) {
  const offset = CIRC * (1 - progress);
  return (
    <svg width={110} height={110} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={55} cy={55} r={R} fill="none" stroke="#ffffff20" strokeWidth={3} />
      <circle
        cx={55} cy={55} r={R}
        fill="none"
        stroke="#ffffffcc"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray={CIRC}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.2s linear' }}
      />
    </svg>
  );
}

// ── Ambient tanpura-like drone — reactive to note & phase ──────────────────
//
// noteFreq : fundamental of the currently sung note (Hz)
// phase    : 'breathe' | 'sing' | 'release'
//
// Behaviour:
//   • On mount: create 5 sine oscillators (Sa + Pa + 3 overtones), fade in
//   • Note change: smoothly glide all oscillator frequencies (0.7s glide)
//   • Phase change:
//       breathe  → moderate gain, upper harmonics pulled back  (warm, inviting swell)
//       sing     → brightest / fullest  (tells the user: "now sing")
//       release  → gentle fade, overtones muted first  (like an exhale)
function useNadaAmbient(noteFreq, phase) {
  const audioRef = useRef(null); // { ctx, master, layers }

  // ── One-time initialisation on mount ──
  useEffect(() => {
    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch { return; }

    const master = ctx.createGain();
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.connect(ctx.destination);

    // Tanpura harmonic series: [ratio, relative gain]
    const CONFIG = [
      { ratio: 1,     g: 1.00 },  // Sa  (fundamental)
      { ratio: 1.498, g: 0.55 },  // Pa  (just fifth)
      { ratio: 2,     g: 0.48 },  // Sa'
      { ratio: 2.997, g: 0.22 },  // Pa'
      { ratio: 4,     g: 0.14 },  // Sa''
    ];
    const BASE = 130.81; // C3 — one octave below the sung Sa

    const layers = CONFIG.map(({ ratio, g }, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = BASE * ratio;
      osc.detune.value = (i % 2 === 0 ? 1 : -1) * i * 0.6; // hair-thin chorus warmth
      gain.gain.value = g;
      osc.connect(gain);
      gain.connect(master);
      osc.start();
      return { osc, gain, ratio, baseGain: g };
    });

    audioRef.current = { ctx, master, layers };
    // Gentle fade-in over ~3 s
    master.gain.setTargetAtTime(0.045, ctx.currentTime, 3);

    return () => {
      if (!audioRef.current) return;
      const { ctx: c, master: m, layers: ls } = audioRef.current;
      const now = c.currentTime;
      m.gain.setTargetAtTime(0, now, 1.5);
      setTimeout(() => {
        ls.forEach(({ osc }) => { try { osc.stop(); } catch {} });
        c.close();
      }, 6000);
      audioRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Glide frequencies to the new note ──
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !noteFreq) return;
    const { ctx, layers } = audio;
    const now = ctx.currentTime;
    // Drone sits an octave below the sung note
    const base = noteFreq / 2;
    layers.forEach(({ osc, ratio }) => {
      // setTargetAtTime creates a smooth exponential glide — gentle, not abrupt
      osc.frequency.setTargetAtTime(base * ratio, now, 0.7);
    });
  }, [noteFreq]);

  // ── Shape timbre & dynamics per phase ──
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const { ctx, master, layers } = audio;
    const now = ctx.currentTime;

    if (phase === 'breathe') {
      // Warm, moderate swell — upper overtones slightly pulled back
      master.gain.setTargetAtTime(0.038, now, 0.7);
      layers.forEach(({ gain, baseGain }, i) => {
        gain.gain.setTargetAtTime(baseGain * (i > 2 ? 0.45 : 0.9), now, 0.7);
      });
    } else if (phase === 'sing') {
      // Fullest, brightest — all harmonics open, clear audio cue to begin
      master.gain.setTargetAtTime(0.075, now, 0.35);
      layers.forEach(({ gain, baseGain }) => {
        gain.gain.setTargetAtTime(baseGain, now, 0.35);
      });
    } else if (phase === 'release') {
      // Gradual exhale — overtones fold away first, fundamental lingers
      master.gain.setTargetAtTime(0.020, now, 1.2);
      layers.forEach(({ gain, baseGain }, i) => {
        gain.gain.setTargetAtTime(baseGain * (i === 0 ? 0.65 : i === 1 ? 0.30 : 0.08), now, 1.2);
      });
    }
  }, [phase]);
}

// ── Main Session Component ───────────────────────────────────────────────────
const NadaSession = ({ mode, onEnd }) => {
  const [noteIndex, setNoteIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [phaseKey, setPhaseKey] = useState(0);
  const [noteKey, setNoteKey] = useState(0);
  const intervalRef = useRef(null);
  const prevPhaseRef = useRef('breathe');

  const notes = mode.notes;
  const currentNote = notes[noteIndex];
  const palette = NOTE_PALETTES[currentNote?.note] || NOTE_PALETTES['Sa'];
  const phase = getPhase(elapsed);

  // Reactive ambient drone — tracks current note frequency and phase
  useNadaAmbient(currentNote?.freq, phase);

  // Voice resonance — drives subtle visual bloom on the background
  const { resonance, energy } = useVoiceResonance(currentNote?.freq);
  // Bloom: 0..1 composite — singing + in-tune = max bloom
  const bloom = useMemo(() => resonance * Math.min(1, energy * 4), [resonance, energy]);

  // Increment timer
  const tick = useCallback(() => {
    setElapsed(prev => {
      const next = prev + 0.1;
      // Phase change detection handled in effect below
      if (next >= PHASE_DURATION) {
        return PHASE_DURATION; // cap, transition handled separately
      }
      return next;
    });
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(tick, 100);
    return () => clearInterval(intervalRef.current);
  }, [tick, noteIndex]);

  // Watch elapsed to drive transitions
  useEffect(() => {
    if (elapsed >= PHASE_DURATION && !transitioning) {
      clearInterval(intervalRef.current);
      setTransitioning(true);
      setTimeout(() => {
        const next = noteIndex + 1;
        if (next >= notes.length) {
          setCompleted(true);
        } else {
          setNoteIndex(next);
          setElapsed(0);
          setNoteKey(k => k + 1);
          setPhaseKey(k => k + 1);
          prevPhaseRef.current = 'breathe';
        }
        setTransitioning(false);
      }, 800);
    }
  }, [elapsed, transitioning, noteIndex, notes.length]);

  // Phase text change
  useEffect(() => {
    if (phase !== prevPhaseRef.current) {
      prevPhaseRef.current = phase;
      setPhaseKey(k => k + 1);
    }
  }, [phase]);

  const progress = elapsed / PHASE_DURATION;

  // ── Completed screen ──
  if (completed) {
    return (
      <Box sx={fullscreenStyle('#080818')}>
        <style>{BLOB_KEYFRAMES}</style>
        {BLOB_DEFS.map((b, i) => (
          <Blob key={i} def={b} colors={['#c084fc', '#a78bfa', '#e879f9']} />
        ))}
        <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 64, mb: 2 }}>॥</Typography>
          <Typography variant="h4" sx={{ color: '#f3e8ff', fontWeight: 300, letterSpacing: 4, mb: 2 }}>
            Śānti
          </Typography>
          <Typography variant="body2" sx={{ color: '#a78bca', letterSpacing: 2, mb: 5 }}>
            The session is complete.
          </Typography>
          <Box
            onClick={onEnd}
            sx={{
              display: 'inline-block',
              px: 4, py: 1.5,
              border: '1px solid #c084fc55',
              borderRadius: 3,
              color: '#c084fc',
              letterSpacing: 3,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              cursor: 'pointer',
              '&:hover': { background: '#c084fc15' },
              transition: 'all 0.2s',
            }}
          >
            Return
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Active session ──
  return (
    <Box sx={fullscreenStyle('#080818')}>
      <style>{BLOB_KEYFRAMES}</style>

      {/* Ambient blobs — opacity lifts gently as user sings in tune */}
      {BLOB_DEFS.map((b, i) => (
        <Blob key={`${noteIndex}-${i}`} def={b} colors={palette} bloom={bloom} />
      ))}

      {/* Resonance glow: radial bloom at center, grows when user sings the note
           mix-blend-mode:screen lets it add light through the vignette without
           covering the content which sits at z:3                               */}
      <Box sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        background: `radial-gradient(ellipse 55% 55% at 50% 50%, ${palette[0]}${Math.round(bloom * 55).toString(16).padStart(2,'0')} 0%, ${palette[1]}${Math.round(bloom * 28).toString(16).padStart(2,'0')} 40%, transparent 70%)`,
        mixBlendMode: 'screen',
        pointerEvents: 'none',
        transition: 'background 0.4s ease',
      }} />

      {/* Central darkness vignette layer */}
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: 'radial-gradient(ellipse 60% 60% at 50% 50%, transparent 30%, #08081899 80%, #080818 100%)',
        pointerEvents: 'none',
      }} />

      {/* Close button */}
      <IconButton
        onClick={onEnd}
        sx={{
          position: 'absolute', top: 16, right: 16, zIndex: 10,
          color: '#ffffffa0',
          '&:hover': { color: '#ffffffd5', background: '#ffffff18' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Progress dots */}
      {notes.length > 1 && (
        <Box sx={{
          position: 'absolute', top: 20, left: 0, right: 0,
          display: 'flex', justifyContent: 'center', gap: 1, zIndex: 10,
        }}>
          {notes.map((n, idx) => (
            <Box key={idx} sx={{
              width: idx === noteIndex ? 20 : 6,
              height: 6,
              borderRadius: 3,
              background: idx < noteIndex ? palette[0] + '99'
                        : idx === noteIndex ? palette[0]
                        : '#ffffff22',
              transition: 'all 0.4s ease',
            }} />
          ))}
        </Box>
      )}

      {/* ── Content ── */}
      <Box
        sx={{
          position: 'relative', zIndex: 3,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          height: '100%', gap: 3, px: 2,
        }}
      >
        {/* Note name — text-shadow blooms when user is in tune */}
        <Typography
          key={`note-${noteKey}`}
          sx={{
            fontSize: { xs: '5rem', md: '7rem' },
            fontWeight: 200,
            color: '#f5eeff',
            letterSpacing: 8,
            lineHeight: 1,
            textShadow: `0 0 ${40 + bloom * 80}px ${palette[0]}${Math.round(40 + bloom * 140).toString(16).padStart(2,'0')}, 0 0 ${80 + bloom * 120}px ${palette[1]}${Math.round(20 + bloom * 80).toString(16).padStart(2,'0')}`,
            animation: 'scaleIn 0.6s ease forwards',
            transition: 'text-shadow 0.4s ease',
          }}
        >
          {currentNote?.note}
        </Typography>

        {/* Frequency label */}
        <Typography sx={{ color: '#ffffff88', fontSize: '0.72rem', letterSpacing: 4, mt: -1 }}>
          {currentNote?.freq ? `${currentNote.freq} Hz` : ''}
        </Typography>

        {/* Arc timer */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <ArcTimer progress={progress} />
        </Box>

        {/* Breathing phase */}
        <Typography
          key={`phase-${phaseKey}`}
          sx={{
            color: phase === 'sing' ? palette[0] : '#d4c0f8',
            letterSpacing: 4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
            fontWeight: phase === 'sing' ? 700 : 400,
            animation: 'fadeSlideUp 0.5s ease forwards',
            mt: -1,
          }}
        >
          {PHASE_TEXT[phase]}
        </Typography>

        {/* Mode name */}
        <Typography sx={{ color: '#ffffff55', fontSize: '0.62rem', letterSpacing: 3, textTransform: 'uppercase', mt: 2 }}>
          {mode.title}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Blob sub-component ────────────────────────────────────────────────────────
function Blob({ def, colors, bloom = 0 }) {
  const [animName, duration, top, left, size, colorIdx] = def;
  // Opacity lifts gently when user is singing in tune (base 0.45 → max 0.67)
  const opacity = 0.45 + bloom * 0.22;
  return (
    <Box
      sx={{
        position: 'absolute',
        top, left,
        width: size,
        height: size,
        borderRadius: '50%',
        background: colors[colorIdx % colors.length],
        filter: `blur(${Math.floor(size * 0.27)}px)`,
        opacity,
        animation: `${animName} ${duration} ease-in-out infinite, pulseAura 5s ease-in-out infinite`,
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform, opacity',
        transition: 'opacity 0.35s ease',
      }}
    />
  );
}

const fullscreenStyle = (bg) => ({
  position: 'fixed', inset: 0,
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  zIndex: 1200,
});

export default NadaSession;
